import type { HistoryEvent, PermissionRequest } from '@gestion-permisos/contracts';
import type { ReactNode } from 'react';
import { useMe } from '../session/session.js';
import { formatCivilDate, formatHours, formatInstant, STATUS_LABELS } from './format.js';

export type FieldRow = [label: string, value: ReactNode];

/**
 * Datos de una solicitud tal como los da la API (horas incluidas: el cliente no las calcula). `leading`
 * añade filas al principio (p. ej., solicitante, aprobador y tipo en la revisión del aprobador, W4).
 */
const FIELDS_BOX = 'mt-4 rounded-md border border-border bg-card p-4';

/** `className` sustituye la caja por defecto (borde, fondo y margen), p. ej. dentro de una tarjeta. */
export function RequestFields({
  request,
  leading = [],
  className = FIELDS_BOX,
}: {
  request: PermissionRequest;
  leading?: FieldRow[];
  className?: string;
}) {
  const rows: FieldRow[] = [
    ...leading,
    ['Fecha del permiso', formatCivilDate(request.permissionDate)],
    ['Hora de salida', request.departureTime],
    ['Hora de regreso', request.returnTime],
    ['Horas solicitadas', formatHours(request.requestedHours)],
  ];
  if (request.actualHours !== null) rows.push(['Duración real', formatHours(request.actualHours)]);
  rows.push(['Motivo', request.reason]);
  if (request.submittedAt !== null) rows.push(['Enviada', formatInstant(request.submittedAt)]);
  if (request.decidedAt !== null) rows.push(['Decidida', formatInstant(request.decidedAt)]);
  // El motivo de rechazo solo existe en el detalle (el listado no lo trae) y solo si viene.
  if (request.rejectionReason !== null) rows.push(['Motivo del rechazo', request.rejectionReason]);
  if (request.cancelledAt !== null) rows.push(['Cancelada', formatInstant(request.cancelledAt)]);

  return (
    <dl className={`grid gap-x-6 gap-y-3 sm:grid-cols-[max-content_1fr] ${className}`}>
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-sm text-muted-foreground">{label}</dt>
          <dd className="whitespace-pre-line">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Historial en orden cronológico (como lo devuelve la API). El autor es «Tú» cuando es el propio usuario.
 * Con `showNames` (revisión del aprobador, W4) se muestra `actor.fullName` de los demás (H-01); sin autor
 * no se muestra nada. Nunca se muestra un identificador como nombre. Con `tokens`, la línea de tiempo de la
 * Power App (lo usan las pantallas ya adaptadas); sin él, cada evento en su recuadro, con los mismos colores.
 */
export function RequestHistory({ events, showNames = false, tokens = false }: { events: HistoryEvent[]; showNames?: boolean; tokens?: boolean }) {
  const me = useMe();
  const muted = 'text-muted-foreground';
  if (events.length === 0) return <p className={muted}>Sin eventos.</p>;
  const author = (event: HistoryEvent) => {
    if (event.actor === null) return null;
    if (event.actor.id === me.id) return 'Tú';
    return showNames ? event.actor.fullName : null;
  };
  return (
    <ol aria-labelledby="historial" className={tokens ? 'space-y-5' : 'mt-3 space-y-3'}>
      {events.map((event) => (
        <li key={event.id} className={tokens ? 'border-l-2 border-l-primary pl-4' : 'rounded-md border border-border bg-card p-3'}>
          <p className={`text-sm ${muted}`}>
            {formatInstant(event.occurredAt)}
            {author(event) !== null ? ` · ${author(event)}` : null}
          </p>
          <p className="mt-1">{event.description}</p>
          {event.previousStatus !== null && event.newStatus !== null && event.previousStatus !== event.newStatus ? (
            <p className={`mt-1 text-sm ${muted}`}>
              {STATUS_LABELS[event.previousStatus]} → {STATUS_LABELS[event.newStatus]}
            </p>
          ) : null}
          {event.comment !== null ? <p className="mt-1 whitespace-pre-line text-sm">Comentario: {event.comment}</p> : null}
        </li>
      ))}
    </ol>
  );
}
